import {Box, BoxProps} from "@mui/material";
import {alpha, Theme} from "@mui/material/styles";
import {forwardRef, InputHTMLAttributes, Ref} from "react";

/** Every line is 40px high; every cell in it is the same 28px box with 14px text, like the app's tables. */
export const FOLIO_ROW_HEIGHT = 40;
export const FOLIO_CELL_HEIGHT = 28;
export const FOLIO_CELL_FONT_SIZE = 14;
/** The text's line in a cell box - its inputs' height too. */
export const FOLIO_CELL_LINE_HEIGHT = 20;

export interface FolioColumn {
    /** The header's translation key; `{{currency}}` is the folio currency's code. */
    label: string
    /** The header's and the cells' alignment: the service reads from the left, every value from the right. */
    align: 'left' | 'right'
    xs: string
    lg: string
}

/**
 * Service · Time · Min · Qty · Price · VAT · Total, then the row menu (no header) - one list for the
 * column header and the lines; the service takes the rest. Price and Total show the amount alone, in
 * the same figures; their headers name the currency once: "Price (EUR)". Sized for the app's table
 * type - 14px cells, 14px medium headers: League Spartan measured at 13px (12px bold headers) and
 * scaled up; a cell box adds 18px (padding and border), the VAT select 32px (with its arrow):
 * - below `lg`: the price fits "1234.50" + a "−10 %" chip, VAT "2,6 % inkl.", the total's header
 *   "Gesamt (EUR)" (wider than "12345.00"), the time input with its picker ("02:00 PM" too); "Menge"
 *   heads the quantity. These 660px fit a tablet - upright in the full-screen dialog (~736px), in
 *   landscape in the dialog (~912px on a 1024px screen); on a phone the lines scroll sideways.
 * - `lg`: the price also fits a "−12,5 %" chip or a 5-digit amount, VAT "2,69 % inkl.". The 1048px
 *   dialog leaves 1000px for the 730px minimum - the rest goes to the service.
 */
export const FOLIO_COLUMNS: FolioColumn[] = [
    {label: 'Service', align: 'left', xs: 'minmax(112px, 1fr)', lg: 'minmax(132px, 1fr)'},
    {label: 'Time', align: 'right', xs: '76px', lg: '92px'},
    {label: 'Min', align: 'right', xs: '44px', lg: '48px'},
    {label: 'Qty', align: 'right', xs: '48px', lg: '52px'},
    {label: 'Price ({{currency}})', align: 'right', xs: '116px', lg: '128px'},
    {label: 'VAT', align: 'right', xs: '96px', lg: '102px'},
    {label: 'Total ({{currency}})', align: 'right', xs: '98px', lg: '106px'},
];
const MENU_COLUMN = '28px';

export const FOLIO_LINE_COLUMNS = {
    xs: [...FOLIO_COLUMNS.map(column => column.xs), MENU_COLUMN].join(' '),
    lg: [...FOLIO_COLUMNS.map(column => column.lg), MENU_COLUMN].join(' '),
};
/** The columns' minimum (gaps included): below it the lines scroll sideways instead of squeezing one. */
export const FOLIO_LINES_MIN_WIDTH = {xs: 660, lg: 730};
export const FOLIO_COLUMN_GAP = 0.75;

export type FolioCellTone = 'default' | 'error' | 'warning';

interface CellBoxProps extends BoxProps {
    /** Holds an input: the outline, hover and focus show. A read-only box keeps the size without them. */
    editable?: boolean
    tone?: FolioCellTone
    align?: 'left' | 'right'
}

const toneColor = (theme: Theme, tone: FolioCellTone) => (
    tone === 'error' ? theme.palette.error.main : tone === 'warning' ? theme.palette.warning.main : undefined
);

/** The one box every editable cell renders - in view and in edit state, so nothing moves between them. */
export const FolioCellBox = forwardRef<HTMLDivElement, CellBoxProps>(
    ({editable = false, tone = 'default', align = 'left', sx, children, ...rest}, ref) => (
        <Box
            ref={ref}
            {...rest}
            sx={[
                theme => {
                    const accent = toneColor(theme, tone);
                    return {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                        gap: 0.5,
                        width: '100%',
                        minWidth: 0,
                        height: FOLIO_CELL_HEIGHT,
                        px: 1,
                        boxSizing: 'border-box',
                        border: '1px solid',
                        borderColor: accent ?? (editable ? alpha(theme.palette.text.primary, 0.23) : 'transparent'),
                        borderRadius: '6px',
                        bgcolor: editable ? 'background.paper' : 'transparent',
                        color: 'text.primary',
                        fontSize: FOLIO_CELL_FONT_SIZE,
                        lineHeight: `${FOLIO_CELL_LINE_HEIGHT}px`,
                        textAlign: align,
                        ...(editable && {
                            cursor: 'text',
                            '&:hover': {borderColor: accent ?? theme.palette.text.primary},
                            '&:focus-within': {
                                borderColor: accent ?? theme.palette.primary.main,
                                boxShadow: `0 0 0 1px ${accent ?? theme.palette.primary.main}`,
                            },
                        }),
                    };
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            {children}
        </Box>
    )
);
FolioCellBox.displayName = 'FolioCellBox';

/** A value in a cell box: one line, cut with an ellipsis (the full text on hover). */
export const FolioCellText = ({value, muted, grow}: {value: string, muted?: boolean, grow?: boolean}) => (
    <Box
        component={'span'}
        title={value}
        sx={{
            flex: grow ? '1 1 auto' : '0 1 auto',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: muted ? 'text.secondary' : undefined,
        }}
    >
        {value}
    </Box>
);

/** An InputBase inside a cell box: no padding of its own, the box's font. */
export const CELL_INPUT_SX = {
    flex: '1 1 auto',
    minWidth: 0,
    fontSize: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    '& .MuiInputBase-input': {p: 0, height: FOLIO_CELL_LINE_HEIGHT, lineHeight: `${FOLIO_CELL_LINE_HEIGHT}px`},
    '& .MuiInputBase-input::placeholder': {color: 'text.secondary', opacity: 1},
};

type AutosizeInputProps = InputHTMLAttributes<HTMLInputElement> & {ref?: Ref<HTMLInputElement>};

/**
 * An input as wide as its text (or placeholder), so what follows it - the service's info icon - sits
 * right after the name. It never grows past the room it has; then the text is cut instead.
 */
export const FolioAutosizeInput = ({sizeText, inputProps}: {sizeText: string, inputProps: AutosizeInputProps}) => (
    <Box
        component={'span'}
        data-value={`${sizeText} `}
        sx={{
            display: 'inline-grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            flex: '0 1 auto',
            minWidth: 0,
            maxWidth: '100%',
            '&::after, & > input': {gridArea: '1 / 1', font: 'inherit', letterSpacing: 'inherit', m: 0, p: 0, minWidth: 0},
            '&::after': {content: 'attr(data-value)', visibility: 'hidden', whiteSpace: 'pre', overflow: 'hidden', height: 0},
            '& > input': {
                width: '100%',
                height: FOLIO_CELL_LINE_HEIGHT,
                border: 0,
                outline: 0,
                bgcolor: 'transparent',
                color: 'inherit',
                textOverflow: 'ellipsis',
                '&::placeholder': {color: 'text.secondary', opacity: 1},
            },
        }}
    >
        {/* size 1: the text sizes the box, not the input's default width */}
        <input size={1} {...inputProps}/>
    </Box>
);
